// Reading the ownership tree out of a flat list of partners.
//
// Ownership questions are the database's to answer — `under_partner_id`,
// `available_to_partner_id`, and a deal's `effective_agreement_id` are all
// recursive walks the API does once and denormalises onto the row. These two
// are the cases where there is no row to read yet: a partner form has to leave
// out the owners a save would be rejected for, and a deal form has to say what
// "inherit" means for a deal that does not exist. Both walk up `parent_id` over
// the partner list the app already fetches, which is why they live together.

import type { Agreement, Partner } from "@crm/shared";

/**
 * `partnerId`, then its owner, then its owner's owner. Stops at a partner the
 * list does not contain: an ancestor nobody fetched is one this walk cannot
 * honestly say anything about.
 *
 * The visited set is not scepticism about the server's cycle guard so much as
 * about the cost of it ever being wrong — a loop here would hang the screen
 * this runs in, while a truncated chain costs one missing option.
 */
export function ownershipChain(partners: readonly Partner[], partnerId: string): Partner[] {
  const byId = new Map(partners.map((partner) => [partner.id, partner]));
  const chain: Partner[] = [];
  const seen = new Set<string>();

  let current = byId.get(partnerId);
  while (current !== undefined && !seen.has(current.id)) {
    seen.add(current.id);
    chain.push(current);
    current = current.parent_id === null ? undefined : byId.get(current.parent_id);
  }
  return chain;
}

/**
 * Who may be offered as `selfId`'s owner: everyone except itself and everything
 * already beneath it. Naming one of those closes an ownership loop, which the
 * server refuses with a `validation_failed` on `parent_id` — so the option is
 * left out of the picker rather than offered and then rejected.
 *
 * `selfId` is null when creating, where nothing is beneath anything yet.
 */
export function eligibleParents(partners: readonly Partner[], selfId: string | null): Partner[] {
  if (selfId === null) return [...partners];
  return partners.filter(
    (partner) =>
      partner.id !== selfId &&
      !ownershipChain(partners, partner.id).some((owner) => owner.id === selfId),
  );
}

/**
 * The agreement a deal under `partnerId` resolves its split through when it
 * pins none of its own: this partner's default, or the nearest owner's above
 * it. Null means nothing resolves — the state the API reports as
 * `split_unresolvable` on any report touching such a deal.
 *
 * A PREDICTION, not the record. The API resolves this itself and publishes the
 * answer as `effective_agreement_id`; this exists for the one moment that field
 * does not exist yet, which is while somebody is filling in the deal form.
 * `agreements` is expected to be the `available_to_partner_id` list — the
 * partner's own agreements plus every owner's — because that is exactly the set
 * this walk can draw a fallback from.
 */
export function inheritedAgreement(
  agreements: readonly Agreement[],
  partners: readonly Partner[],
  partnerId: string,
): Agreement | null {
  for (const owner of ownershipChain(partners, partnerId)) {
    const fallback = agreements.find(
      (agreement) => agreement.partner_id === owner.id && agreement.is_default,
    );
    if (fallback !== undefined) return fallback;
  }
  return null;
}
